import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/utils/api';

export type ClientDocumentsConfig = {
  documentsNasRoot: string;
  appBaseUrl: string;
  maintenanceMode: boolean;
  notificationDaysBefore: number;
};

const DEFAULT_CONFIG: ClientDocumentsConfig = {
  documentsNasRoot: '\\\\NAS_GORI\\dati_nas\\Public\\Volume_1',
  appBaseUrl: 'https://vps-3dee2600.vps.ovh.net/',
  maintenanceMode: false,
  notificationDaysBefore: 0,
};

let cached: ClientDocumentsConfig | null = null;

export async function fetchClientDocumentsConfig(): Promise<ClientDocumentsConfig> {
  try {
    const { data } = await apiClient.get<{ success: boolean; data: ClientDocumentsConfig }>(
      '/settings/client-config'
    );
    if (data.success && data.data) {
      cached = { ...DEFAULT_CONFIG, ...data.data };
      return cached;
    }
  } catch {
    // offline o non autenticato
  }
  return cached ?? DEFAULT_CONFIG;
}

/** Config server per NAS, URL app, manutenzione, notifiche (richiede sessione). */
export function useClientDocumentsConfig() {
  const [config, setConfig] = useState<ClientDocumentsConfig | null>(cached);
  const [loading, setLoading] = useState(!cached);

  const reload = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      const c = await fetchClientDocumentsConfig();
      setConfig(c);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    const onChanged = () => void reload({ silent: true });
    window.addEventListener('settings:changed', onChanged);
    return () => window.removeEventListener('settings:changed', onChanged);
  }, [reload]);

  const merged = config ?? DEFAULT_CONFIG;

  return {
    ...merged,
    loading,
    reload,
    config: merged,
  };
}
